/**
 * CustomPatternBuilder - Drag-and-drop builder for custom suggestion patterns
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Close as RemoveIcon,
} from '@mui/icons-material';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export type PatternElementType = 'TITLE' | 'MS_NO' | 'AUTHOR' | 'SEPARATOR' | 'MEDIA';
export type PatternMapTo = 'title' | 'author' | 'ms_no' | 'media' | 'ignore';

export interface PatternBuilderElement {
  id: string;
  type: PatternElementType;
  maps_to: PatternMapTo;
}

interface CustomPatternBuilderProps {
  elements: PatternBuilderElement[];
  onChange: (elements: PatternBuilderElement[]) => void;
  errors?: string[];
}

const PALETTE: PatternElementType[] = ['TITLE', 'MS_NO', 'AUTHOR', 'SEPARATOR', 'MEDIA'];

const defaultMappingForType = (type: PatternElementType): PatternMapTo => {
  switch (type) {
    case 'TITLE':
      return 'title';
    case 'MS_NO':
      return 'ms_no';
    case 'AUTHOR':
      return 'author';
    case 'MEDIA':
      return 'media';
    case 'SEPARATOR':
    default:
      return 'ignore';
  }
};

const mappingOptionsForType = (type: PatternElementType): PatternMapTo[] => {
  switch (type) {
    case 'MEDIA':
      return ['media', 'ignore'];
    case 'SEPARATOR':
      return ['ignore'];
    default:
      return ['title', 'author', 'ms_no', 'ignore'];
  }
};

const getChipColor = (type: PatternElementType): 'primary' | 'warning' | 'success' | 'default' => {
  switch (type) {
    case 'TITLE':
    case 'MS_NO':
    case 'AUTHOR':
      return 'primary';
    case 'SEPARATOR':
      return 'warning';
    case 'MEDIA':
      return 'success';
    default:
      return 'default';
  }
};

const SortableRow: React.FC<{
  element: PatternBuilderElement;
  onRemove: (id: string) => void;
  onMapChange: (id: string, maps_to: PatternMapTo) => void;
}> = ({ element, onRemove, onMapChange }) => {
  const { t } = useTranslation('components');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: element.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'grab' }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" color="action" />
        <Chip label={t(`customPatternBuilder.patternElementTypes.${element.type}`)} size="small" color={getChipColor(element.type)} />
      </Box>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>{t('customPatternBuilder.mapsTo')}</InputLabel>
        <Select
          value={element.maps_to}
          label={t('customPatternBuilder.mapsTo')}
          onChange={(event) => onMapChange(element.id, event.target.value as PatternMapTo)}
        >
          {mappingOptionsForType(element.type).map((option) => (
            <MenuItem key={option} value={option}>
              {t(`customPatternBuilder.mapsToOptions.${option}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ flexGrow: 1 }} />
      <IconButton size="small" onClick={() => onRemove(element.id)}>
        <RemoveIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
};

const CustomPatternBuilder: React.FC<CustomPatternBuilderProps> = ({
  elements,
  onChange,
  errors = [],
}) => {
  const { t } = useTranslation('components');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleAdd = (type: PatternElementType) => {
    const next: PatternBuilderElement = {
      id: `${type}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      type,
      maps_to: defaultMappingForType(type),
    };
    onChange([...elements, next]);
  };

  const handleRemove = (id: string) => {
    onChange(elements.filter((element) => element.id !== id));
  };

  const handleMapChange = (id: string, maps_to: PatternMapTo) => {
    onChange(
      elements.map((element) => (element.id === id ? { ...element, maps_to } : element))
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = elements.findIndex((element) => element.id === active.id);
    const newIndex = elements.findIndex((element) => element.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    onChange(arrayMove(elements, oldIndex, newIndex));
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t('customPatternBuilder.title')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {PALETTE.map((type) => (
          <Button key={type} variant="outlined" size="small" onClick={() => handleAdd(type)}>
            {t('customPatternBuilder.add', { type: t(`customPatternBuilder.patternElementTypes.${type}`) })}
          </Button>
        ))}
      </Box>

      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <SortableContext items={elements.map((element) => element.id)} strategy={verticalListSortingStrategy}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {elements.length === 0 && (
              <Paper sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                {t('customPatternBuilder.emptyMessage')}
              </Paper>
            )}
            {elements.map((element) => (
              <SortableRow
                key={element.id}
                element={element}
                onRemove={handleRemove}
                onMapChange={handleMapChange}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>

      {errors.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {errors.map((error, index) => (
            <Typography key={index} variant="caption" color="error" display="block">
              {error}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CustomPatternBuilder;
